// Admin tab — Passeurs (Lumière / Dieu / Baie / Histoire & sciences / Autres)
// Each category automatically publishes the fiche on the matching public page:
//   • artiste            → /presence/artistes        — "Passeurs de Lumière"
//   • religieux          → /presence/passeurs-foi    — "Passeurs de l'Indicible"
//   • conferencier+autre → /presence/passeurs        — "Passeurs de la Baie"
//   • histoire_sciences  → /presence/passeurs-histoire-sciences
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

// Known honorific titles — used to auto-detect the "titre" prefix when migrating legacy names.
const _HONORIFICS = new Set(["m.", "mme", "père", "pere", "frère", "frere", "sœur", "soeur", "abbé", "abbe", "mgr", "dr", "prof.", "prof"]);

// Split a legacy full name into [titre, prenom, nom].
// "Père Maciej Leszczynski" → ("Père", "Maciej", "Leszczynski")
// "Renara Akhoundova"        → ("",     "Renara",  "Akhoundova")
// Single-word names → ("", "", word).
const _splitParts = (full) => {
  const tokens = (full || "").trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { titre: "", prenom: "", nom: "" };
  if (tokens.length === 1) return { titre: "", prenom: "", nom: tokens[0] };
  let titre = "";
  if (_HONORIFICS.has(tokens[0].toLowerCase())) {
    titre = tokens.shift();
  }
  const nom = tokens.pop();
  const prenom = tokens.join(" ");
  return { titre, prenom, nom };
};
const _splitPrenom = (full) => _splitParts(full).prenom;
const _splitNom = (full) => _splitParts(full).nom;
const _splitTitre = (full) => _splitParts(full).titre;

const CATEGORIES = [
  { value: "artiste",           fr: "Passeurs de Lumière",                en: "Bearers of Light"            },
  { value: "religieux",         fr: "Passeurs de l'Indicible",            en: "Bearers of the Ineffable"    },
  { value: "conferencier",      fr: "Passeurs de la Baie",                en: "Bearers of the Bay"          },
  { value: "histoire_sciences", fr: "Passeurs de l'histoire et des sciences", en: "Bearers of History & Sciences" },
  { value: "autre",             fr: "Autre (rattaché à la Baie)",         en: "Other (Bay)"                 },
];

const categoryLabel = (val, isFr) => {
  const c = CATEGORIES.find((x) => x.value === val);
  return c ? (isFr ? c.fr : c.en) : val;
};

export default function IntervenantsTab({ locale }) {
  const isFr = locale === "fr";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | artiste | conferencier | autre
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/intervenants");
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (it) => {
    await api.patch(`/admin/intervenants/${it.id}`, { active: !it.active });
    load();
  };

  const remove = async (it) => {
    if (!window.confirm(isFr ? `Retirer définitivement « ${it.name} » ?` : `Permanently remove "${it.name}"?`)) return;
    await api.delete(`/admin/intervenants/${it.id}`);
    load();
  };

  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState("");

  const exportJson = async () => {
    setImportError("");
    try {
      const res = await api.get("/admin/intervenants/export");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `passeurs-${ts}.json`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (ex) {
      setImportError(ex?.response?.data?.detail || "Export error");
    }
  };

  const importJson = async (file) => {
    setImportBusy(true); setImportError(""); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("by", "id");
      fd.append("wipe", "false");
      const { data } = await api.post("/admin/intervenants/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      load();
    } catch (ex) {
      setImportError(ex?.response?.data?.detail || "Import error");
    } finally { setImportBusy(false); }
  };

  const visible = filter === "all" ? items : items.filter((i) => i.category === filter);

  return (
    <div data-testid="intervenants-tab" className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl">{isFr ? "Passeurs" : "Bearers"}</h2>
          <p className="font-body text-sm" style={{ color: "var(--muted)" }}>
            {isFr
              ? "Toutes les fiches passeurs — chaque catégorie alimente automatiquement la page publique correspondante (Lumière, Dieu, Baie, Histoire & sciences)."
              : "All bearer fiches — each category automatically feeds the matching public page."}
          </p>
        </div>
        <button data-testid="intervenant-new" onClick={() => setEditing({})} className="btn btn-primary">
          + {isFr ? "Nouveau passeur" : "New bearer"}
        </button>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="intervenants-filter">
        {[
          { v: "all",               lbl: isFr ? "Tous" : "All" },
          { v: "artiste",           lbl: isFr ? "Passeurs de Lumière" : "Bearers of Light" },
          { v: "religieux",         lbl: isFr ? "Passeurs de l'Indicible" : "Bearers of the Ineffable" },
          { v: "conferencier",      lbl: isFr ? "Passeurs de la Baie" : "Bearers of the Bay" },
          { v: "histoire_sciences", lbl: isFr ? "Histoire & sciences" : "History & sciences" },
          { v: "autre",             lbl: isFr ? "Autres" : "Others" },
        ].map((f) => (
          <button key={f.v}
                  data-testid={`intervenants-filter-${f.v}`}
                  onClick={() => setFilter(f.v)}
                  className="font-caps text-[0.65rem] px-3 py-1.5 border"
                  style={{
                    background: filter === f.v ? "var(--ink)" : "transparent",
                    color: filter === f.v ? "white" : "var(--ink)",
                    borderColor: "var(--ink)",
                    letterSpacing: "0.18em",
                  }}>
            {f.lbl}
          </button>
        ))}
      </div>

      {loading && <p className="text-center" style={{ color: "var(--muted)" }}>{isFr ? "Chargement…" : "Loading…"}</p>}

      {/* ---- Migration tools (Export / Import JSON) ---- */}
      <div className="bg-white border p-4 sm:p-5" style={{ borderColor: "var(--border)" }} data-testid="intervenants-migration">
        <div className="flex items-baseline justify-between mb-2 flex-wrap gap-3">
          <div>
            <div className="label-caps">{isFr ? "MIGRATION JSON" : "JSON MIGRATION"}</div>
            <p className="font-body text-xs mt-1" style={{ color: "var(--muted)" }}>
              {isFr
                ? "Export du carnet complet vers JSON (sauvegarde, migration preview → production). Import upsert par `id` — non destructif."
                : "Export the full address book to JSON (backup, preview → production migration). Import upserts by `id` — non destructive."}
            </p>
          </div>
          <div className="flex gap-3 flex-wrap items-center">
            <button type="button"
                    data-testid="intervenants-export-btn"
                    onClick={exportJson}
                    className="font-caps text-[0.62rem] px-3 py-2 border"
                    style={{ borderColor: "var(--lapis)", color: "var(--lapis)", letterSpacing: "0.2em" }}>
              ⇩ {isFr ? "EXPORTER JSON" : "EXPORT JSON"}
            </button>
            <label className="font-caps text-[0.62rem] px-3 py-2 border cursor-pointer"
                   style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)", letterSpacing: "0.2em" }}>
              ⇧ {isFr ? "IMPORTER JSON" : "IMPORT JSON"}
              <input data-testid="intervenants-import-file" type="file" accept="application/json,.json"
                     className="hidden" disabled={importBusy}
                     onChange={(e) => {
                       const f = e.target.files?.[0];
                       if (f) importJson(f);
                       e.target.value = "";
                     }} />
            </label>
          </div>
        </div>
        {importBusy && (
          <p className="font-body italic text-sm mt-2" style={{ color: "var(--muted)" }}>
            {isFr ? "Import en cours…" : "Importing…"}
          </p>
        )}
        {importError && (
          <p className="font-body text-sm mt-2" style={{ color: "var(--cinnabar)" }} data-testid="intervenants-import-error">
            {importError}
          </p>
        )}
        {importResult && (
          <div className="font-body text-sm mt-3 p-3 border-l-2"
               style={{ borderColor: "var(--gold)", background: "var(--parchment-2)" }}
               data-testid="intervenants-import-result">
            <div className="font-caps text-[0.62rem]" style={{ color: "var(--gold)", letterSpacing: "0.2em" }}>
              {isFr ? "IMPORT TERMINÉ" : "IMPORT DONE"}
            </div>
            <div className="mt-1">
              <strong>{importResult.inserted}</strong> {isFr ? "ajouté(s)" : "added"} ·{" "}
              <strong>{importResult.updated}</strong> {isFr ? "mis à jour" : "updated"} ·{" "}
              <strong>{importResult.skipped}</strong> {isFr ? "ignoré(s)" : "skipped"}
              {importResult.deleted > 0 && (
                <> · <strong style={{ color: "var(--cinnabar)" }}>{importResult.deleted}</strong> {isFr ? "supprimé(s)" : "deleted"}</>
              )}
              {" · "} {isFr ? "total" : "total"} {importResult.total}
            </div>
            {Array.isArray(importResult.errors) && importResult.errors.length > 0 && (
              <ul className="mt-2 list-disc pl-5" style={{ color: "var(--cinnabar)" }}>
                {importResult.errors.map((er, i) => <li key={i}>{er}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      {!loading && visible.length === 0 && (
        <p className="text-center bg-white border p-8" style={{ borderColor: "var(--border)", color: "var(--muted)" }}>
          {isFr ? "Aucun passeur enregistré." : "No bearers yet."}
        </p>
      )}

      {!loading && visible.length > 0 && (
        <div className="bg-white border" style={{ borderColor: "var(--border)" }}>
          <table className="w-full font-body text-sm">
            <thead>
              <tr style={{ background: "var(--parchment-2)" }}>
                <th className="text-left p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "PHOTO" : "PHOTO"}</th>
                <th className="text-left p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "NOM" : "NAME"}</th>
                <th className="text-left p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "CATÉGORIE" : "CATEGORY"}</th>
                <th className="text-left p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "FONCTION" : "FUNCTION"}</th>
                <th className="text-left p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "VILLE" : "CITY"}</th>
                <th className="text-center p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "STATUT" : "STATUS"}</th>
                <th className="text-right p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((it) => (
                <tr key={it.id} data-testid={`intervenant-row-${it.id}`}
                    className="border-t" style={{ borderColor: "var(--border)" }}>
                  <td className="p-3">
                    {it.photo_url ? (
                      <img src={it.photo_url} alt={it.name} className="h-12 w-12 object-cover border" style={{ borderColor: "var(--border)" }} />
                    ) : (
                      <div className="h-12 w-12 inline-flex items-center justify-center font-display"
                           style={{ background: "var(--parchment-2)", color: "var(--gold)" }}>
                        {(it.name || "?").charAt(0)}
                      </div>
                    )}
                  </td>
                  <td className="p-3 font-display" style={{ color: "var(--ink)" }}>{it.name}</td>
                  <td className="p-3">
                    <span className="font-caps text-[0.6rem] px-2 py-0.5 border"
                          style={{
                            borderColor: it.category === "artiste" ? "var(--cinnabar)"
                                       : it.category === "conferencier" ? "var(--lapis)"
                                       : it.category === "religieux" ? "var(--gold)"
                                       : it.category === "histoire_sciences" ? "var(--moss)"
                                       : "var(--muted)",
                            color: it.category === "artiste" ? "var(--cinnabar)"
                                 : it.category === "conferencier" ? "var(--lapis)"
                                 : it.category === "religieux" ? "var(--gold)"
                                 : it.category === "histoire_sciences" ? "var(--moss)"
                                 : "var(--muted)",
                            letterSpacing: "0.18em",
                          }}>
                      {categoryLabel(it.category, isFr)}
                    </span>
                  </td>
                  <td className="p-3 text-xs" style={{ color: "var(--ink-2)" }}>{it.function || "—"}</td>
                  <td className="p-3 text-xs" style={{ color: "var(--muted)" }}>
                    {[it.city, it.country].filter(Boolean).join(", ") || "—"}
                  </td>
                  <td className="p-3 text-center">
                    <button data-testid={`intervenant-active-${it.id}`}
                            onClick={() => toggleActive(it)}
                            className="font-caps text-[0.6rem] px-2.5 py-1 border"
                            style={{
                              borderColor: it.active ? "var(--moss)" : "var(--muted)",
                              color: it.active ? "var(--moss)" : "var(--muted)",
                              letterSpacing: "0.15em",
                            }}>
                      {it.active ? (isFr ? "ACTIF" : "ACTIVE") : (isFr ? "MASQUÉ" : "HIDDEN")}
                    </button>
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      <button data-testid={`intervenant-edit-${it.id}`} onClick={() => setEditing(it)}
                              className="font-caps text-[0.6rem] px-2.5 py-1 border"
                              style={{ borderColor: "var(--lapis)", color: "var(--lapis)", letterSpacing: "0.15em" }}>
                        {isFr ? "MODIFIER" : "EDIT"}
                      </button>
                      <button data-testid={`intervenant-delete-${it.id}`} onClick={() => remove(it)}
                              className="font-caps text-[0.6rem] px-2.5 py-1 border"
                              style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)", letterSpacing: "0.15em" }}>
                        {isFr ? "SUPPR." : "DEL."}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <IntervenantEditor
          intervenant={editing}
          locale={locale}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function IntervenantEditor({ intervenant, locale, onClose, onSaved }) {
  const isFr = locale === "fr";
  const isNew = !intervenant.id;
  const [form, setForm] = useState({
    category: intervenant.category || "artiste",
    name: intervenant.name || "",
    titre: intervenant.titre || _splitTitre(intervenant.name),
    prenom: intervenant.prenom || _splitPrenom(intervenant.name),
    nom: intervenant.nom || _splitNom(intervenant.name),
    slug: intervenant.slug || "",
    function: intervenant.function || "",
    function_label: intervenant.function_label || "",
    bio: intervenant.bio || "",
    city: intervenant.city || "",
    country: intervenant.country || "",
    website: intervenant.website || "",
    instagram: intervenant.instagram || "",
    youtube: intervenant.youtube || "",
    facebook: intervenant.facebook || "",
    links: Array.isArray(intervenant.links) ? intervenant.links : [],
    upcoming_events: Array.isArray(intervenant.upcoming_events) ? intervenant.upcoming_events : [],
    active: intervenant.active !== false,
    order: intervenant.order || 0,
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [gallery, setGallery] = useState(Array.isArray(intervenant.gallery) ? intervenant.gallery : []);
  const [galleryBusy, setGalleryBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const GALLERY_LIMIT = 12;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // ---- Custom links handlers (label + url pairs) ----
  const addLink = () => set("links", [...(form.links || []), { label: "", url: "" }]);
  const updateLink = (idx, key, value) => {
    const next = [...(form.links || [])];
    next[idx] = { ...next[idx], [key]: value };
    set("links", next);
  };
  const removeLink = (idx) => {
    const next = [...(form.links || [])];
    next.splice(idx, 1);
    set("links", next);
  };

  // ---- Upcoming events handlers (title + date + location + url) ----
  const addEvent = () => set("upcoming_events", [
    ...(form.upcoming_events || []),
    { title: "", date: "", location: "", url: "" },
  ]);
  const updateEvent = (idx, key, value) => {
    const next = [...(form.upcoming_events || [])];
    next[idx] = { ...next[idx], [key]: value };
    set("upcoming_events", next);
  };
  const removeEvent = (idx) => {
    const next = [...(form.upcoming_events || [])];
    next.splice(idx, 1);
    set("upcoming_events", next);
  };

  // ---- Gallery handlers (uploaded immediately to backend on file pick) ----
  const uploadGalleryPhoto = async (file, caption = "") => {
    if (!intervenant.id) {
      setErr(isFr
        ? "Enregistrez d'abord la fiche, puis ajoutez les photos de la galerie."
        : "Save the fiche first, then add gallery photos.");
      return;
    }
    if (gallery.length >= GALLERY_LIMIT) {
      setErr(isFr ? `Galerie pleine (${GALLERY_LIMIT} photos maximum).` : `Gallery full (${GALLERY_LIMIT} photos max).`);
      return;
    }
    setGalleryBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      const { data } = await api.post(
        `/admin/intervenants/${intervenant.id}/gallery`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setGallery(data.gallery || []);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Erreur upload");
    } finally { setGalleryBusy(false); }
  };

  const removeGalleryPhoto = async (idx) => {
    if (!intervenant.id) return;
    if (!window.confirm(isFr ? "Retirer cette photo ?" : "Remove this photo?")) return;
    setGalleryBusy(true);
    try {
      const { data } = await api.delete(`/admin/intervenants/${intervenant.id}/gallery/${idx}`);
      setGallery(data.gallery || []);
    } finally { setGalleryBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      // Filter out empty links / events before saving
      const cleanLinks = (form.links || [])
        .map((l) => ({ label: (l.label || "").trim(), url: (l.url || "").trim() }))
        .filter((l) => l.label && l.url);
      const cleanEvents = (form.upcoming_events || [])
        .map((ev) => ({
          title: (ev.title || "").trim(),
          date: (ev.date || "").trim(),
          location: (ev.location || "").trim(),
          url: (ev.url || "").trim(),
        }))
        .filter((ev) => ev.title);
      const payload = { ...form, links: cleanLinks, upcoming_events: cleanEvents };
      // Recompose the canonical `name` from prenom + nom (backend also recomposes; we send a value so the
      // frontend list ('name' column) stays in sync without a re-fetch).
      const prenom = (payload.prenom || "").trim();
      const nom = (payload.nom || "").trim();
      payload.name = (prenom || nom) ? `${prenom} ${nom}`.trim() : (payload.name || "").trim();
      if (!payload.slug) delete payload.slug; // let backend auto-generate
      let id = intervenant.id;
      if (isNew) {
        const { data } = await api.post("/admin/intervenants", payload);
        id = data.id;
      } else {
        await api.patch(`/admin/intervenants/${id}`, payload);
      }
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        await api.post(`/admin/intervenants/${id}/photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      onSaved();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="intervenant-editor"
         onClick={onClose}
         className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
         style={{ background: "rgba(20,16,12,0.6)" }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white border max-w-2xl w-full p-6 space-y-4 my-8"
            style={{ borderColor: "var(--border)", boxShadow: "0 20px 40px rgba(0,0,0,0.3)" }}>
        <div className="font-caps text-[0.65rem]" style={{ color: "var(--gold)", letterSpacing: "0.25em" }}>
          {isNew ? (isFr ? "NOUVEL INTERVENANT" : "NEW CONTRIBUTOR") : (isFr ? "MODIFIER" : "EDIT")}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="label-caps">{isFr ? "CATÉGORIE *" : "CATEGORY *"}</span>
            <select data-testid="intervenant-category" required
                    className="input-classic mt-1" style={{ width: "100%" }}
                    value={form.category} onChange={(e) => set("category", e.target.value)}>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{isFr ? c.fr : c.en}</option>
              ))}
            </select>
            <span className="font-body text-xs mt-1 block" style={{ color: "var(--muted)" }}>
              {isFr
                ? "Chaque fiche est automatiquement publiée sur la page correspondante du site selon sa catégorie : « Passeurs de Lumière » → /presence/artistes · « Passeurs de l'Indicible » → /presence/passeurs-foi · « Passeurs de la Baie » & « Autre » → /presence/passeurs · « Passeurs de l'histoire et des sciences » → /presence/passeurs-histoire-sciences."
                : "Each fiche is automatically published on the corresponding page based on its category."}
            </span>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr_1fr] gap-3">
            <label className="block">
              <span className="label-caps">{isFr ? "TITRE" : "TITLE"}</span>
              <input data-testid="intervenant-titre"
                     list="intervenant-titre-options"
                     placeholder={isFr ? "M. / Mme / Père…" : "Mr / Mrs / Father…"}
                     className="input-classic mt-1" style={{ width: "100%" }}
                     value={form.titre} onChange={(e) => set("titre", e.target.value)} />
              <datalist id="intervenant-titre-options">
                <option value="M." />
                <option value="Mme" />
                <option value="Père" />
                <option value="Frère" />
                <option value="Sœur" />
                <option value="Abbé" />
                <option value="Mgr" />
                <option value="Dr" />
                <option value="Prof." />
              </datalist>
            </label>
            <label className="block">
              <span className="label-caps">{isFr ? "PRÉNOM" : "FIRST NAME"}</span>
              <input data-testid="intervenant-prenom"
                     className="input-classic mt-1" style={{ width: "100%" }}
                     value={form.prenom} onChange={(e) => set("prenom", e.target.value)} />
            </label>
            <label className="block">
              <span className="label-caps">{isFr ? "NOM *" : "LAST NAME *"}</span>
              <input data-testid="intervenant-nom" required
                     className="input-classic mt-1" style={{ width: "100%" }}
                     value={form.nom} onChange={(e) => set("nom", e.target.value)} />
            </label>
          </div>

          <label className="block">
            <span className="label-caps">{isFr ? "FONCTION / SPÉCIALITÉ" : "FUNCTION / SPECIALTY"}</span>
            <input data-testid="intervenant-function"
                   placeholder={form.category === "artiste"
                     ? (isFr ? "ex. Fresque, Sculpture, Musique" : "e.g. Fresco, Sculpture, Music")
                     : (isFr ? "ex. Histoire médiévale, Théologie, Patrimoine" : "e.g. Medieval history, Theology, Heritage")}
                   className="input-classic mt-1" style={{ width: "100%" }}
                   value={form.function} onChange={(e) => set("function", e.target.value)} />
            <span className="font-body text-xs mt-1 block" style={{ color: "var(--muted)" }}>
              {isFr ? "Sert aussi à regrouper les cartes sur la page publique." : "Used to group cards on the public page."}
            </span>
          </label>

          <label className="block">
            <span className="label-caps">{isFr ? "ORDRE D'AFFICHAGE" : "DISPLAY ORDER"}</span>
            <input type="number" data-testid="intervenant-order"
                   className="input-classic mt-1" style={{ width: "100%" }}
                   value={form.order} onChange={(e) => set("order", Number(e.target.value) || 0)} />
          </label>

          <label className="block">
            <span className="label-caps">{isFr ? "VILLE" : "CITY"}</span>
            <input className="input-classic mt-1" style={{ width: "100%" }}
                   value={form.city} onChange={(e) => set("city", e.target.value)} />
          </label>

          <label className="block">
            <span className="label-caps">{isFr ? "PAYS" : "COUNTRY"}</span>
            <input className="input-classic mt-1" style={{ width: "100%" }}
                   value={form.country} onChange={(e) => set("country", e.target.value)} />
          </label>
        </div>

        <label className="block">
          <span className="label-caps">{isFr ? "BIOGRAPHIE / PRÉSENTATION" : "BIO / INTRODUCTION"}</span>
          <textarea data-testid="intervenant-bio" rows={4}
                    className="input-classic mt-1" style={{ width: "100%" }}
                    value={form.bio} onChange={(e) => set("bio", e.target.value)} />
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="label-caps">{isFr ? "SITE WEB" : "WEBSITE"}</span>
            <input type="url" placeholder="https://…"
                   className="input-classic mt-1" style={{ width: "100%" }}
                   value={form.website} onChange={(e) => set("website", e.target.value)} />
          </label>
          <label className="block">
            <span className="label-caps">INSTAGRAM</span>
            <input className="input-classic mt-1" style={{ width: "100%" }}
                   value={form.instagram} onChange={(e) => set("instagram", e.target.value)} />
          </label>
          <label className="block">
            <span className="label-caps">YOUTUBE</span>
            <input className="input-classic mt-1" style={{ width: "100%" }}
                   value={form.youtube} onChange={(e) => set("youtube", e.target.value)} />
          </label>
          <label className="block">
            <span className="label-caps">FACEBOOK</span>
            <input className="input-classic mt-1" style={{ width: "100%" }}
                   value={form.facebook} onChange={(e) => set("facebook", e.target.value)} />
          </label>
        </div>

        <label className="block">
          <span className="label-caps">{isFr ? "PHOTO (PNG, JPG, WEBP — max 20 Mo)" : "PHOTO (PNG, JPG, WEBP — max 20 MB)"}</span>
          <input data-testid="intervenant-photo-file" type="file" accept=".png,.jpg,.jpeg,.webp"
                 className="mt-1 font-body text-sm"
                 onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} />
          {intervenant.photo_url && !photoFile && (
            <img src={intervenant.photo_url} alt="" className="h-24 mt-2 border" style={{ borderColor: "var(--border)" }} />
          )}
        </label>

        {/* ---- Gallery (Au Prieuré) ---- */}
        <div className="border-t pt-4" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-baseline justify-between mb-2">
            <span className="label-caps">{isFr ? "GALERIE « AU PRIEURÉ » (max 12 photos)" : "GALLERY (max 12 photos)"}</span>
            <span className="font-body text-xs" style={{ color: "var(--muted)" }}>
              {gallery.length}/12
            </span>
          </div>
          <p className="font-body text-xs mb-3" style={{ color: "var(--muted)" }}>
            {isFr
              ? "Photos d'ambiance affichées sous la biographie (concerts, expositions, portraits, lieux). La 1ʳᵉ photo sert de visuel principal, les suivantes apparaissent en miniatures."
              : "Atmosphere photos displayed below the bio (concerts, exhibitions, portraits, places)."}
          </p>
          {!intervenant.id && (
            <p className="font-body text-xs italic" style={{ color: "var(--cinnabar)" }}>
              {isFr ? "Enregistrez d'abord la fiche pour activer la galerie." : "Save the fiche first to enable the gallery."}
            </p>
          )}
          {intervenant.id && (
            <>
              {gallery.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3" data-testid="intervenant-gallery-grid">
                  {gallery.map((g, idx) => (
                    <div key={`${g.url}-${idx}`} className="relative group" data-testid={`intervenant-gallery-item-${idx}`}>
                      <img src={g.url} alt={g.caption || `Photo ${idx + 1}`}
                           className="w-full h-24 object-cover border" style={{ borderColor: "var(--border)" }} />
                      {idx === 0 && (
                        <span className="absolute top-1 left-1 font-caps text-[0.55rem] px-1.5 py-0.5"
                              style={{ background: "var(--gold)", color: "var(--ink)", letterSpacing: "0.15em" }}>
                          {isFr ? "PRINCIPAL" : "MAIN"}
                        </span>
                      )}
                      <button type="button"
                              data-testid={`intervenant-gallery-remove-${idx}`}
                              onClick={() => removeGalleryPhoto(idx)}
                              disabled={galleryBusy}
                              className="absolute top-1 right-1 w-6 h-6 inline-flex items-center justify-center font-caps text-xs"
                              style={{ background: "var(--cinnabar)", color: "white" }}
                              aria-label={isFr ? "Supprimer" : "Remove"}>
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {gallery.length < 12 && (
                <input data-testid="intervenant-gallery-file" type="file" accept=".png,.jpg,.jpeg,.webp"
                       className="font-body text-sm" disabled={galleryBusy}
                       onChange={(e) => {
                         const f = e.target.files?.[0];
                         if (f) uploadGalleryPhoto(f);
                         e.target.value = "";
                       }} />
              )}
            </>
          )}
        </div>

        {/* ---- Custom links (label + url) ---- */}
        <div className="border-t pt-4" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-baseline justify-between mb-2">
            <span className="label-caps">{isFr ? "LIENS INTERNET PERSONNALISÉS" : "CUSTOM INTERNET LINKS"}</span>
            <button type="button"
                    data-testid="intervenant-link-add"
                    onClick={addLink}
                    className="font-caps text-[0.6rem] px-2.5 py-1 border"
                    style={{ borderColor: "var(--lapis)", color: "var(--lapis)", letterSpacing: "0.15em" }}>
              + {isFr ? "AJOUTER" : "ADD"}
            </button>
          </div>
          <p className="font-body text-xs mb-3" style={{ color: "var(--muted)" }}>
            {isFr
              ? "Liens additionnels (Bandcamp, Spotify, article, concert au Prieuré 2027…). Affichés sous la biographie en liste cliquable."
              : "Additional links (Bandcamp, Spotify, article, concert at the Priory 2027…). Displayed under the bio as a clickable list."}
          </p>
          {(form.links || []).length === 0 && (
            <p className="font-body text-xs italic" style={{ color: "var(--muted)" }}>
              {isFr ? "Aucun lien personnalisé." : "No custom links yet."}
            </p>
          )}
          {(form.links || []).map((lk, idx) => (
            <div key={idx} className="flex gap-2 mb-2" data-testid={`intervenant-link-row-${idx}`}>
              <input className="input-classic" style={{ flex: "0 0 35%" }}
                     placeholder={isFr ? "Libellé (ex. Bandcamp)" : "Label (e.g. Bandcamp)"}
                     value={lk.label} onChange={(e) => updateLink(idx, "label", e.target.value)} />
              <input className="input-classic" style={{ flex: 1 }} type="url"
                     placeholder="https://…"
                     value={lk.url} onChange={(e) => updateLink(idx, "url", e.target.value)} />
              <button type="button"
                      data-testid={`intervenant-link-remove-${idx}`}
                      onClick={() => removeLink(idx)}
                      className="font-caps text-[0.6rem] px-2.5 border"
                      style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)", letterSpacing: "0.15em" }}>
                ×
              </button>
            </div>
          ))}
        </div>

        {/* ---- Upcoming events (agenda) ---- */}
        <div className="border-t pt-4" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-baseline justify-between mb-2">
            <span className="label-caps">{isFr ? "ÉVÉNEMENTS À VENIR (AGENDA)" : "UPCOMING EVENTS (AGENDA)"}</span>
            <button type="button"
                    data-testid="intervenant-event-add"
                    onClick={addEvent}
                    className="font-caps text-[0.6rem] px-2.5 py-1 border"
                    style={{ borderColor: "var(--lapis)", color: "var(--lapis)", letterSpacing: "0.15em" }}>
              + {isFr ? "AJOUTER" : "ADD"}
            </button>
          </div>
          <p className="font-body text-xs mb-3" style={{ color: "var(--muted)" }}>
            {isFr
              ? "Concerts, conférences, retraites… Affichés sur la fiche individuelle /passeurs/<slug>."
              : "Concerts, talks, retreats… Displayed on the individual page /passeurs/<slug>."}
          </p>
          {(form.upcoming_events || []).length === 0 && (
            <p className="font-body text-xs italic" style={{ color: "var(--muted)" }}>
              {isFr ? "Aucun événement à venir." : "No upcoming events yet."}
            </p>
          )}
          {(form.upcoming_events || []).map((ev, idx) => (
            <div key={idx} className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 p-3 border"
                 style={{ borderColor: "var(--border)" }}
                 data-testid={`intervenant-event-row-${idx}`}>
              <input className="input-classic"
                     placeholder={isFr ? "Titre (ex. Concert d'ouverture)" : "Title (e.g. Opening concert)"}
                     value={ev.title} onChange={(e) => updateEvent(idx, "title", e.target.value)} />
              <input className="input-classic"
                     placeholder={isFr ? "Date (ex. Samedi 3 juillet 2027 — 20h30)" : "Date (e.g. Saturday 3 July 2027 — 8:30 pm)"}
                     value={ev.date} onChange={(e) => updateEvent(idx, "date", e.target.value)} />
              <input className="input-classic"
                     placeholder={isFr ? "Lieu (ex. Prieuré saint Léonard)" : "Location"}
                     value={ev.location} onChange={(e) => updateEvent(idx, "location", e.target.value)} />
              <div className="flex gap-2">
                <input className="input-classic" style={{ flex: 1 }} type="url"
                       placeholder="https://… (optionnel)"
                       value={ev.url} onChange={(e) => updateEvent(idx, "url", e.target.value)} />
                <button type="button"
                        data-testid={`intervenant-event-remove-${idx}`}
                        onClick={() => removeEvent(idx)}
                        className="font-caps text-[0.6rem] px-2.5 border"
                        style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)", letterSpacing: "0.15em" }}>
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* ---- Slug (URL personnalisée) ---- */}
        <div className="border-t pt-4" style={{ borderColor: "var(--border)" }}>
          <label className="block">
            <span className="label-caps">{isFr ? "URL DE LA FICHE (SLUG)" : "PAGE URL (SLUG)"}</span>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-body text-xs" style={{ color: "var(--muted)" }}>/passeurs/</span>
              <input data-testid="intervenant-slug"
                     placeholder={isFr ? "renara-akhoundova (auto si vide)" : "renara-akhoundova (auto if empty)"}
                     className="input-classic" style={{ flex: 1 }}
                     value={form.slug} onChange={(e) => set("slug", e.target.value)} />
            </div>
            <span className="font-body text-xs mt-1 block" style={{ color: "var(--muted)" }}>
              {isFr
                ? "Si laissé vide, l'URL est générée automatiquement depuis le nom."
                : "Leave empty to auto-generate from the name."}
            </span>
          </label>
        </div>

        <label className="flex items-center gap-2 font-body text-sm">
          <input type="checkbox" checked={form.active}
                 onChange={(e) => set("active", e.target.checked)} />
          {isFr ? "Afficher publiquement sur le site" : "Display publicly"}
        </label>

        {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn btn-outline" style={{ flex: 1 }}>
            {isFr ? "Annuler" : "Cancel"}
          </button>
          <button type="submit" data-testid="intervenant-save" disabled={busy}
                  className="btn btn-primary" style={{ flex: 1 }}>
            {busy ? "…" : (isFr ? "Enregistrer" : "Save")}
          </button>
        </div>
      </form>
    </div>
  );
}
